import React, { useState, useRef, useEffect, useMemo } from 'react';
import { animated, useSprings, useSpring } from '@react-spring/web';
import { useDrag } from '@use-gesture/react';
import { X } from 'lucide-react';

interface EmotionCheckInProps {
  onClose: () => void;
  onComplete: (data: EmotionEntry) => void;
}

interface EmotionEntry {
  quadrant: string;
  emotion: string;
  activities: string[];
  companions: string[];
  textNote?: string;
}

interface EmotionBubble {
  mood: string;
  color: string;
  size: 'small' | 'medium' | 'large';
  shape?: 'circle' | 'square' | 'diamond' | 'oval' | 'heart' | 'hexagon';
  x?: number;
  y?: number;
  vx?: number;
  vy?: number;
}

// Extended emotion set with special shapes and richer variety
const moodQuadrants: Record<string, EmotionBubble[]> = {
  'high-pleasant': [
    { mood: 'Excited', color: 'rgb(var(--color-accent-500))', size: 'large', shape: 'heart' },
    { mood: 'Joyful', color: 'rgb(var(--color-accent-400))', size: 'large' },
    { mood: 'Happy', color: 'rgb(var(--color-accent-300))', size: 'medium' },
    { mood: 'Elated', color: 'rgb(var(--color-accent-500))', size: 'medium', shape: 'diamond' },
    { mood: 'Cheerful', color: 'rgb(var(--color-accent-500))', size: 'small' },
    { mood: 'Thrilled', color: 'rgb(var(--color-accent-500))', size: 'small' },
    { mood: 'Energetic', color: 'rgb(var(--color-accent-400))', size: 'medium' },
    { mood: 'Optimistic', color: 'rgb(var(--color-accent-300))', size: 'small' },
    { mood: 'Enthusiastic', color: 'rgb(var(--color-accent-400))', size: 'medium' },
    { mood: 'Delighted', color: 'rgb(var(--color-accent-200))', size: 'small' },
    { mood: 'Vibrant', color: 'rgb(var(--color-accent-500))', size: 'medium' },
    { mood: 'Alive', color: 'rgb(var(--color-accent-300))', size: 'small' },
    { mood: 'Radiant', color: 'rgb(var(--color-warning-400))', size: 'medium', shape: 'square' },
    { mood: 'Blissful', color: 'rgb(var(--color-warning-300))', size: 'large' },
    { mood: 'Ecstatic', color: 'rgb(var(--color-accent-600))', size: 'medium' },
    { mood: 'Playful', color: 'rgb(var(--color-accent-200))', size: 'small' },
    { mood: 'Gleeful', color: 'rgb(var(--color-accent-300))', size: 'small' },
    { mood: 'Euphoric', color: 'rgb(var(--color-accent-500))', size: 'medium' },
    { mood: 'Buoyant', color: 'rgb(var(--color-accent-200))', size: 'small' },
    { mood: 'Exhilarated', color: 'rgb(var(--color-accent-500))', size: 'large' },
    { mood: 'Upbeat', color: 'rgb(var(--color-accent-300))', size: 'small' },
    { mood: 'Zestful', color: 'rgb(var(--color-accent-400))', size: 'medium' },
    { mood: 'Inspired', color: 'rgb(var(--color-warning-200))', size: 'medium', shape: 'hexagon' },
    { mood: 'Motivated', color: 'rgb(var(--color-warning-300))', size: 'medium' },
    { mood: 'Passionate', color: 'rgb(var(--color-accent-600))', size: 'large' },
    { mood: 'Determined', color: 'rgb(var(--color-accent-600))', size: 'medium' },
    { mood: 'Eager', color: 'rgb(var(--color-warning-400))', size: 'small' },
    { mood: 'Confident', color: 'rgb(var(--color-warning-200))', size: 'medium' },
    { mood: 'Proud', color: 'rgb(var(--color-warning-300))', size: 'medium', shape: 'square' },
    { mood: 'Accomplished', color: 'rgb(var(--color-accent-400))', size: 'large' },
    { mood: 'Victorious', color: 'rgb(var(--color-accent-600))', size: 'large', shape: 'diamond' },
    { mood: 'Triumphant', color: 'rgb(var(--color-warning-400))', size: 'large' },
    { mood: 'Hopeful', color: 'rgb(var(--color-accent-100))', size: 'medium' },
    { mood: 'Grateful', color: 'rgb(var(--color-accent-200))', size: 'small' },
    { mood: 'Blessed', color: 'rgb(var(--color-warning-300))', size: 'medium' },
    { mood: 'Lucky', color: 'rgb(var(--color-accent-400))', size: 'small' },
    { mood: 'Amazed', color: 'rgb(var(--color-accent-500))', size: 'medium' },
    { mood: 'Wonderful', color: 'rgb(var(--color-warning-200))', size: 'medium' },
    { mood: 'Fantastic', color: 'rgb(var(--color-accent-300))', size: 'large' },
    { mood: 'Marvelous', color: 'rgb(var(--color-accent-400))', size: 'medium' },
    { mood: 'Excellent', color: 'rgb(var(--color-accent-200))', size: 'medium' },
    { mood: 'Superb', color: 'rgb(var(--color-accent-400))', size: 'small' },
  ],
  'high-unpleasant': [
    { mood: 'Angry', color: 'rgb(var(--color-secondary-500))', size: 'large', shape: 'square' },
    { mood: 'Anxious', color: 'rgb(var(--color-secondary-400))', size: 'large' },
    { mood: 'Frustrated', color: 'rgb(var(--color-secondary-500))', size: 'medium' },
    { mood: 'Stressed', color: 'rgb(var(--color-secondary-300))', size: 'medium' },
    { mood: 'Overwhelmed', color: 'rgb(var(--color-secondary-500))', size: 'small' },
    { mood: 'Agitated', color: 'rgb(var(--color-secondary-600))', size: 'small' },
    { mood: 'Nervous', color: 'rgb(var(--color-secondary-300))', size: 'medium' },
    { mood: 'Tense', color: 'rgb(var(--color-secondary-400))', size: 'small' },
    { mood: 'Irritated', color: 'rgb(var(--color-error-300))', size: 'medium' },
    { mood: 'Annoyed', color: 'rgb(var(--color-error-200))', size: 'small' },
    { mood: 'Furious', color: 'rgb(var(--color-error-700))', size: 'large', shape: 'diamond' },
    { mood: 'Enraged', color: 'rgb(var(--color-error-800))', size: 'large' },
    { mood: 'Livid', color: 'rgb(var(--color-error-900))', size: 'medium' },
    { mood: 'Outraged', color: 'rgb(var(--color-error-600))', size: 'medium' },
    { mood: 'Hostile', color: 'rgb(var(--color-error-600))', size: 'medium' },
    { mood: 'Restless', color: 'rgb(var(--color-secondary-300))', size: 'small' },
    { mood: 'Panicked', color: 'rgb(var(--color-error-400))', size: 'medium' },
    { mood: 'Alarmed', color: 'rgb(var(--color-error-500))', size: 'small' },
    { mood: 'Worried', color: 'rgb(var(--color-secondary-400))', size: 'medium' },
    { mood: 'Fearful', color: 'rgb(var(--color-error-400))', size: 'medium' },
    { mood: 'Terrified', color: 'rgb(var(--color-error-600))', size: 'large' },
    { mood: 'Horrified', color: 'rgb(var(--color-error-700))', size: 'medium' },
    { mood: 'Distressed', color: 'rgb(var(--color-error-500))', size: 'medium' },
    { mood: 'Uneasy', color: 'rgb(var(--color-secondary-300))', size: 'small' },
    { mood: 'Uncomfortable', color: 'rgb(var(--color-secondary-200))', size: 'small' },
    { mood: 'Triggered', color: 'rgb(var(--color-error-500))', size: 'medium', shape: 'hexagon' },
    { mood: 'Aggravated', color: 'rgb(var(--color-error-400))', size: 'medium' },
    { mood: 'Exasperated', color: 'rgb(var(--color-secondary-400))', size: 'medium' },
    { mood: 'Vexed', color: 'rgb(var(--color-secondary-300))', size: 'small' },
    { mood: 'Provoked', color: 'rgb(var(--color-error-600))', size: 'medium' },
    { mood: 'Incensed', color: 'rgb(var(--color-error-700))', size: 'large' },
    { mood: 'Irate', color: 'rgb(var(--color-error-800))', size: 'medium' },
    { mood: 'Mad', color: 'rgb(var(--color-error-500))', size: 'medium' },
    { mood: 'Bothered', color: 'rgb(var(--color-secondary-200))', size: 'small' },
    { mood: 'Disturbed', color: 'rgb(var(--color-secondary-400))', size: 'medium' },
    { mood: 'Perturbed', color: 'rgb(var(--color-error-400))', size: 'small' },
    { mood: 'Ruffled', color: 'rgb(var(--color-secondary-200))', size: 'small' },
    { mood: 'Troubled', color: 'rgb(var(--color-error-400))', size: 'medium' },
    { mood: 'Upset', color: 'rgb(var(--color-error-500))', size: 'medium' },
    { mood: 'Rattled', color: 'rgb(var(--color-secondary-300))', size: 'small' },
    { mood: 'Shaken', color: 'rgb(var(--color-secondary-400))', size: 'medium' },
    { mood: 'Unsettled', color: 'rgb(var(--color-error-400))', size: 'small' },
  ],
  'low-pleasant': [
    { mood: 'Calm', color: 'rgb(var(--color-primary-500))', size: 'large', shape: 'oval' },
    { mood: 'Relaxed', color: 'rgb(var(--color-primary-300))', size: 'large' },
    { mood: 'Content', color: 'rgb(var(--color-primary-300))', size: 'medium' },
    { mood: 'Peaceful', color: 'rgb(var(--color-primary-400))', size: 'medium', shape: 'circle' },
    { mood: 'Serene', color: 'rgb(var(--color-primary-200))', size: 'small' },
    { mood: 'Comfortable', color: 'rgb(var(--color-primary-500))', size: 'small' },
    { mood: 'Satisfied', color: 'rgb(var(--color-primary-200))', size: 'medium' },
    { mood: 'Grateful', color: 'rgb(var(--color-primary-300))', size: 'small' },
    { mood: 'Tranquil', color: 'rgb(var(--color-success-300))', size: 'medium' },
    { mood: 'Mellow', color: 'rgb(var(--color-success-200))', size: 'small' },
    { mood: 'Placid', color: 'rgb(var(--color-primary-300))', size: 'medium' },
    { mood: 'Composed', color: 'rgb(var(--color-success-400))', size: 'small' },
    { mood: 'Collected', color: 'rgb(var(--color-success-300))', size: 'medium' },
    { mood: 'Centered', color: 'rgb(var(--color-success-200))', size: 'medium', shape: 'hexagon' },
    { mood: 'Grounded', color: 'rgb(var(--color-success-300))', size: 'medium' },
    { mood: 'Balanced', color: 'rgb(var(--color-success-300))', size: 'medium' },
    { mood: 'Stable', color: 'rgb(var(--color-success-300))', size: 'small' },
    { mood: 'Secure', color: 'rgb(var(--color-success-300))', size: 'medium' },
    { mood: 'Safe', color: 'rgb(var(--color-success-200))', size: 'small' },
    { mood: 'Protected', color: 'rgb(var(--color-success-300))', size: 'medium' },
    { mood: 'Cozy', color: 'rgb(var(--color-success-100))', size: 'small', shape: 'heart' },
    { mood: 'Snug', color: 'rgb(var(--color-success-100))', size: 'small' },
    { mood: 'Warm', color: 'rgb(var(--color-success-200))', size: 'medium' },
    { mood: 'Tender', color: 'rgb(var(--color-success-200))', size: 'medium' },
    { mood: 'Gentle', color: 'rgb(var(--color-success-200))', size: 'small' },
    { mood: 'Soft', color: 'rgb(var(--color-success-100))', size: 'small' },
    { mood: 'Mild', color: 'rgb(var(--color-success-200))', size: 'small' },
    { mood: 'Easy', color: 'rgb(var(--color-success-200))', size: 'medium' },
    { mood: 'Light', color: 'rgb(var(--color-success-100))', size: 'small' },
    { mood: 'Pleasant', color: 'rgb(var(--color-success-200))', size: 'medium' },
    { mood: 'Nice', color: 'rgb(var(--color-success-200))', size: 'small' },
    { mood: 'Good', color: 'rgb(var(--color-success-300))', size: 'medium' },
    { mood: 'Fine', color: 'rgb(var(--color-success-200))', size: 'small' },
    { mood: 'Okay', color: 'rgb(var(--color-success-300))', size: 'small' },
    { mood: 'Alright', color: 'rgb(var(--color-success-200))', size: 'medium' },
    { mood: 'Decent', color: 'rgb(var(--color-success-300))', size: 'small' },
    { mood: 'Fair', color: 'rgb(var(--color-success-200))', size: 'small' },
    { mood: 'Adequate', color: 'rgb(var(--color-success-200))', size: 'medium' },
    { mood: 'Sufficient', color: 'rgb(var(--color-success-300))', size: 'medium' },
    { mood: 'Rested', color: 'rgb(var(--color-success-300))', size: 'medium' },
    { mood: 'Refreshed', color: 'rgb(var(--color-success-400))', size: 'large' },
    { mood: 'Restored', color: 'rgb(var(--color-success-400))', size: 'medium' },
  ],
  'low-unpleasant': [
    { mood: 'Sad', color: 'rgb(var(--color-base-500))', size: 'large', shape: 'oval' },
    { mood: 'Tired', color: 'rgb(var(--color-base-400))', size: 'large' },
    { mood: 'Lonely', color: 'rgb(var(--color-base-600))', size: 'medium' },
    { mood: 'Depressed', color: 'rgb(var(--color-base-700))', size: 'medium', shape: 'square' },
    { mood: 'Bored', color: 'rgb(var(--color-base-400))', size: 'small' },
    { mood: 'Melancholy', color: 'rgb(var(--color-base-600))', size: 'small' },
    { mood: 'Exhausted', color: 'rgb(var(--color-base-400))', size: 'medium' },
    { mood: 'Drained', color: 'rgb(var(--color-base-500))', size: 'small' },
    { mood: 'Gloomy', color: 'rgb(var(--color-base-600))', size: 'medium' },
    { mood: 'Dismal', color: 'rgb(var(--color-base-700))', size: 'small' },
    { mood: 'Bleak', color: 'rgb(var(--color-base-800))', size: 'medium' },
    { mood: 'Somber', color: 'rgb(var(--color-base-600))', size: 'small' },
    { mood: 'Morose', color: 'rgb(var(--color-base-700))', size: 'medium' },
    { mood: 'Dejected', color: 'rgb(var(--color-base-600))', size: 'medium' },
    { mood: 'Downcast', color: 'rgb(var(--color-base-500))', size: 'small' },
    { mood: 'Despondent', color: 'rgb(var(--color-base-700))', size: 'medium' },
    { mood: 'Hopeless', color: 'rgb(var(--color-base-800))', size: 'medium' },
    { mood: 'Helpless', color: 'rgb(var(--color-base-700))', size: 'small' },
    { mood: 'Powerless', color: 'rgb(var(--color-base-600))', size: 'medium' },
    { mood: 'Weak', color: 'rgb(var(--color-base-500))', size: 'small' },
    { mood: 'Vulnerable', color: 'rgb(var(--color-base-600))', size: 'medium' },
    { mood: 'Fragile', color: 'rgb(var(--color-base-600))', size: 'small' },
    { mood: 'Broken', color: 'rgb(var(--color-base-800))', size: 'medium', shape: 'diamond' },
    { mood: 'Hurt', color: 'rgb(var(--color-base-700))', size: 'medium' },
    { mood: 'Wounded', color: 'rgb(var(--color-base-700))', size: 'medium' },
    { mood: 'Damaged', color: 'rgb(var(--color-base-800))', size: 'small' },
    { mood: 'Empty', color: 'rgb(var(--color-base-500))', size: 'medium' },
    { mood: 'Hollow', color: 'rgb(var(--color-base-600))', size: 'small' },
    { mood: 'Void', color: 'rgb(var(--color-base-700))', size: 'medium' },
    { mood: 'Numb', color: 'rgb(var(--color-base-500))', size: 'small' },
    { mood: 'Lifeless', color: 'rgb(var(--color-base-600))', size: 'medium' },
    { mood: 'Fatigued', color: 'rgb(var(--color-base-500))', size: 'medium' },
    { mood: 'Weary', color: 'rgb(var(--color-base-500))', size: 'small' },
    { mood: 'Spent', color: 'rgb(var(--color-base-600))', size: 'medium' },
    { mood: 'Burnt Out', color: 'rgb(var(--color-base-600))', size: 'large' },
    { mood: 'Sluggish', color: 'rgb(var(--color-base-500))', size: 'small' },
    { mood: 'Lethargic', color: 'rgb(var(--color-base-500))', size: 'medium' },
    { mood: 'Listless', color: 'rgb(var(--color-base-500))', size: 'small' },
    { mood: 'Apathetic', color: 'rgb(var(--color-base-600))', size: 'medium' },
    { mood: 'Indifferent', color: 'rgb(var(--color-base-600))', size: 'small' },
    { mood: 'Detached', color: 'rgb(var(--color-base-600))', size: 'medium' },
    { mood: 'Disconnected', color: 'rgb(var(--color-base-700))', size: 'medium' },
  ],
};

const activities = [
  'Working', 'Exercising', 'Eating', 'Relaxing', 'Socializing',
  'Learning', 'Creating', 'Playing', 'Traveling', 'Resting',
  'Shopping', 'Cooking', 'Cleaning', 'Commuting', 'Watching',
  'Reading', 'Gaming', 'Studying', 'Meeting', 'Walking'
];

const companions = [
  'By Myself', 'Friends', 'Family', 'Colleagues', 'Partner',
  'Children', 'Pets', 'Strangers', 'Classmates', 'Team'
];

export const EmotionCheckIn: React.FC<EmotionCheckInProps> = ({ onClose, onComplete }) => {
  const [step, setStep] = useState<'quadrant' | 'emotion' | 'context'>('quadrant');
  const [selectedQuadrant, setSelectedQuadrant] = useState<string>('');
  const [selectedEmotion, setSelectedEmotion] = useState<string>('');
  const [selectedActivities, setSelectedActivities] = useState<string[]>([]);
  const [selectedCompanions, setSelectedCompanions] = useState<string[]>([]);
  const [textNote, setTextNote] = useState<string>('');
  
  const canGoBack = step !== 'quadrant';
  
  const handleBack = () => {
    if (step === 'emotion') {
      setStep('quadrant');
      setSelectedEmotion('');
    } else if (step === 'context') {
      setStep(selectedQuadrant === 'unknown' ? 'quadrant' : 'emotion');
    }
  };

  const handleQuadrantSelect = (quadrant: string) => {
    setSelectedQuadrant(quadrant);
    if (quadrant === 'unknown') {
      setSelectedEmotion('unknown');
      setStep('context');
    } else {
      setStep('emotion');
    }
  };

  const handleEmotionSelect = (emotion: string) => {
    setSelectedEmotion(emotion);
    setStep('context');
  };

  const handleComplete = () => {
    onComplete({
      quadrant: selectedQuadrant,
      emotion: selectedEmotion,
      activities: selectedActivities,
      companions: selectedCompanions,
      textNote: textNote.trim() || undefined,
    });
  };

  return (
    <div className="fixed inset-0 bg-base-950/95 z-50 flex items-center justify-center">
      <div className="w-full max-w-6xl mx-auto p-6">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div className="flex items-center gap-4">
            {canGoBack && (
              <button
                onClick={handleBack}
                className="p-2 hover:bg-base-100/10 rounded-full transition-colors"
                aria-label="Go back"
              >
                <svg className="w-6 h-6 text-base-100" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
            )}
            <div>
              <h2 className="text-2xl font-bold text-base-100">
                {step === 'quadrant' && 'How are you feeling?'}
                {step === 'emotion' && 'Select your emotion'}
                {step === 'context' && 'Add some context'}
              </h2>
              <p className="text-base-100/60 text-sm mt-1">
                Step {step === 'quadrant' ? 1 : step === 'emotion' ? 2 : 3} of 3
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-base-100/10 rounded-full transition-colors"
            aria-label="Close"
          >
            <X className="w-6 h-6 text-base-100" />
          </button>
        </div>

        {/* Step 1: Quadrant Selection */}
        {step === 'quadrant' && (
          <QuadrantSelector onSelect={handleQuadrantSelect} />
        )}

        {/* Step 2: Emotion Selection */}
        {step === 'emotion' && selectedQuadrant && (
          <EmotionBubbles
            quadrant={selectedQuadrant}
            onSelect={handleEmotionSelect}
          />
        )}

        {/* Step 3: Context */}
        {step === 'context' && (
          <ContextForm
            activities={activities}
            companions={companions}
            selectedActivities={selectedActivities}
            selectedCompanions={selectedCompanions}
            textNote={textNote}
            onActivitiesChange={setSelectedActivities}
            onCompanionsChange={setSelectedCompanions}
            onTextNoteChange={setTextNote}
            onComplete={handleComplete}
          />
        )}
      </div>
    </div>
  );
};

// Quadrant Selector Component
const QuadrantSelector: React.FC<{ onSelect: (quadrant: string) => void }> = ({ onSelect }) => {
  const quadrants = [
    { id: 'high-pleasant', label: 'High Energy\nPleasant', gradient: 'oklch(from var(--color-warning) l c h / 0.8)', gradientTo: 'var(--color-warning)' },
    { id: 'high-unpleasant', label: 'High Energy\nUnpleasant', gradient: 'oklch(from var(--color-error) l c h / 0.8)', gradientTo: 'var(--color-error)' },
    { id: 'low-pleasant', label: 'Low Energy\nPleasant', gradient: 'oklch(from var(--color-success) l c h / 0.8)', gradientTo: 'var(--color-success)' },
    { id: 'low-unpleasant', label: 'Low Energy\nUnpleasant', gradient: 'oklch(from var(--color-info) l c h / 0.8)', gradientTo: 'var(--color-info)' },
  ];

  return (
    <div className="relative">
      <div className="grid grid-cols-2 gap-8 max-w-2xl mx-auto">
        {quadrants.map((quadrant) => (
          <button
            key={quadrant.id}
            onClick={() => onSelect(quadrant.id)}
            className="p-12 font-medium hover:scale-105 active:scale-95 transition-all duration-200 shadow-2xl h-48 flex items-center justify-center relative overflow-hidden group"
            style={{
              background: `linear-gradient(to bottom right, ${quadrant.gradient}, ${quadrant.gradientTo})`,
              color: quadrant.id.includes('pleasant') && !quadrant.id.includes('unpleasant') ? 
                     (quadrant.id.includes('high') ? 'var(--color-warning-content)' : 'var(--color-success-content)') :
                     (quadrant.id.includes('high') ? 'var(--color-error-content)' : 'var(--color-info-content)'),
              borderRadius: 'var(--radius-box)'
            }}
          >
            <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300" style={{ backgroundColor: 'oklch(from var(--color-base-100) l c h / 0.1)' }} />
            <span className="whitespace-pre-line text-center text-lg font-semibold relative z-10">{quadrant.label}</span>
          </button>
        ))}
      </div>

      {/* Center "I don't know" button */}
      <button
        onClick={() => onSelect('unknown')}
        className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-32 h-32 rounded-full shadow-2xl hover:scale-110 active:scale-95 transition-all duration-200 font-medium text-base flex items-center justify-center group"
        style={{
          background: `linear-gradient(to bottom right, var(--color-base-300), var(--color-neutral))`,
          color: 'var(--color-base-content)'
        }}
      >
        <div className="absolute inset-0 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300" style={{ backgroundColor: 'oklch(from var(--color-base-100) l c h / 0.1)' }} />
        <span className="relative z-10">I don't<br />know</span>
      </button>
    </div>
  );
};

// Advanced Emotion Bubbles Component with Infinite Grid and Physics
const EmotionBubbles: React.FC<{
  quadrant: string;
  onSelect: (emotion: string) => void;
}> = ({ quadrant, onSelect }) => {
  const emotions = moodQuadrants[quadrant] || [];
  const containerRef = useRef<HTMLDivElement>(null);
  const [focusedBubble, setFocusedBubble] = useState<string | null>(null);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [viewportSize, setViewportSize] = useState({ width: 0, height: 0 });
  const animationFrameRef = useRef<number>();
  const lastMousePosRef = useRef({ x: 0, y: 0 });
  const isDraggingRef = useRef(false);
  const draggedBubbleRef = useRef<string | null>(null);
  
  // Constants for the infinite grid
  const BUBBLE_SIZE = 112; // Fixed size as per spec
  const BUBBLE_SPACING = 14;
  const GRID_SIZE = BUBBLE_SIZE + BUBBLE_SPACING;
  const HEXAGON_VERTICAL_SPACING = 0.866; // sqrt(3)/2 for perfect hexagonal packing
  
  // Update viewport size
  useEffect(() => {
    const updateSize = () => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        setViewportSize({ width: rect.width, height: rect.height });
      }
    };
    updateSize();
    window.addEventListener('resize', updateSize);
    return () => window.removeEventListener('resize', updateSize);
  }, []);
  
  // Generate bubble positions with hexagonal packing in infinite grid
  const generateBubblePositions = useMemo(() => {
    const positions: Map<string, { x: number; y: number; emotion: EmotionBubble }> = new Map();
    
    // Calculate visible area with buffer for smooth scrolling
    const buffer = 2; // Number of extra rows/cols to render outside viewport
    const visibleLeft = -pan.x - (viewportSize.width / 2 / zoom) - buffer * GRID_SIZE;
    const visibleRight = -pan.x + (viewportSize.width / 2 / zoom) + buffer * GRID_SIZE;
    const visibleTop = -pan.y - (viewportSize.height / 2 / zoom) - buffer * GRID_SIZE;
    const visibleBottom = -pan.y + (viewportSize.height / 2 / zoom) + buffer * GRID_SIZE;
    
    // Calculate grid bounds
    const startCol = Math.floor(visibleLeft / GRID_SIZE);
    const endCol = Math.ceil(visibleRight / GRID_SIZE);
    const startRow = Math.floor(visibleTop / (GRID_SIZE * HEXAGON_VERTICAL_SPACING));
    const endRow = Math.ceil(visibleBottom / (GRID_SIZE * HEXAGON_VERTICAL_SPACING));
    
    // Place bubbles in hexagonal grid pattern
    let emotionIndex = 0;
    for (let row = startRow; row <= endRow; row++) {
      for (let col = startCol; col <= endCol; col++) {
        const offsetX = (row % 2) * (GRID_SIZE / 2);
        const x = col * GRID_SIZE + offsetX;
        const y = row * GRID_SIZE * HEXAGON_VERTICAL_SPACING;
        
        // Use modulo to repeat emotions in infinite pattern
        const emotion = emotions[Math.abs(emotionIndex) % emotions.length];
        const key = `${emotion.mood}_${row}_${col}`;
        
        positions.set(key, { x, y, emotion });
        emotionIndex++;
      }
    }
    
    return positions;
  }, [emotions, pan, zoom, viewportSize]);
  
  // Physics simulation for bubble interactions
  const [bubbleStates, setBubbleStates] = useState<Map<string, { 
    dx: number; 
    dy: number; 
    scale: number;
    rotation: number;
  }>>(new Map());
  
  // Create springs for all visible bubbles
  const springConfigs = Array.from(generateBubblePositions.entries()).map(([key, pos]) => ({
    key,
    x: pos.x + (bubbleStates.get(key)?.dx || 0),
    y: pos.y + (bubbleStates.get(key)?.dy || 0),
    scale: bubbleStates.get(key)?.scale || 1,
    rotation: bubbleStates.get(key)?.rotation || 0,
    config: { tension: 300, friction: 30 }
  }));
  
  const [springs, api] = useSprings(springConfigs.length, (i) => springConfigs[i]);
  
  // Update springs when bubble states change
  useEffect(() => {
    api.start((i) => {
      const config = springConfigs[i];
      if (!config) return {};
      
      const state = bubbleStates.get(config.key);
      return {
        x: config.x,
        y: config.y,
        scale: state?.scale || 1,
        rotation: state?.rotation || 0,
      };
    });
  }, [bubbleStates, springConfigs, api]);
  
  // Mouse/touch tracking for focus detection
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (containerRef.current && !isDraggingRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        const x = (e.clientX - rect.left - rect.width / 2) / zoom - pan.x;
        const y = (e.clientY - rect.top - rect.height / 2) / zoom - pan.y;
        
        lastMousePosRef.current = { x, y };
        
        // Find closest bubble to cursor for focus effect
        let closestBubble: string | null = null;
        let closestDistance = Infinity;
        
        generateBubblePositions.forEach((pos, key) => {
          const dx = x - pos.x;
          const dy = y - pos.y;
          const distance = Math.sqrt(dx * dx + dy * dy);
          
          if (distance < BUBBLE_SIZE / 2 && distance < closestDistance) {
            closestDistance = distance;
            closestBubble = key;
          }
        });
        
        setFocusedBubble(closestBubble);
        
        // Apply soft-body physics to nearby bubbles
        const newStates = new Map(bubbleStates);
        generateBubblePositions.forEach((pos, key) => {
          const dx = x - pos.x;
          const dy = y - pos.y;
          const distance = Math.sqrt(dx * dx + dy * dy);
          
          if (distance < BUBBLE_SIZE * 2) {
            const force = Math.max(0, 1 - distance / (BUBBLE_SIZE * 2));
            const angle = Math.atan2(dy, dx);
            const pushX = -Math.cos(angle) * force * 15;
            const pushY = -Math.sin(angle) * force * 15;
            
            newStates.set(key, {
              dx: pushX,
              dy: pushY,
              scale: closestBubble === key ? 1.1 : 1 - force * 0.1,
              rotation: (pushX + pushY) * 0.1
            });
          } else {
            newStates.set(key, {
              dx: 0,
              dy: 0,
              scale: 1,
              rotation: 0
            });
          }
        });
        
        setBubbleStates(newStates);
      }
    };
    
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, [generateBubblePositions, zoom, pan, bubbleStates]);
  
  // Pan and zoom handlers
  const bindPan = useDrag(({ movement: [mx, my], first, memo = { x: pan.x, y: pan.y } }) => {
    if (first) {
      isDraggingRef.current = true;
    }
    
    setPan({
      x: memo.x + mx / zoom,
      y: memo.y + my / zoom
    });
    
    return memo;
  }, {
    filterTaps: true,
    threshold: 10
  });
  
  // Wheel handler for zoom
  useEffect(() => {
    const handleWheel = (e: WheelEvent) => {
      if (containerRef.current?.contains(e.target as Node)) {
        e.preventDefault();
        const delta = e.deltaY * -0.001;
        setZoom(prev => Math.max(0.5, Math.min(2, prev + delta)));
      }
    };
    
    window.addEventListener('wheel', handleWheel, { passive: false });
    return () => window.removeEventListener('wheel', handleWheel);
  }, []);
  
  // Render shape based on emotion configuration
  const renderBubbleShape = (emotion: EmotionBubble, isActive: boolean) => {
    const baseClass = `absolute inset-0 flex items-center justify-center transition-opacity duration-200 ${isActive ? 'opacity-100' : 'opacity-90'}`;
    
    switch (emotion.shape) {
      case 'square':
        return (
          <div className={`${baseClass} rounded-2xl`} style={{ backgroundColor: emotion.color }}>
            <span className="font-medium text-base-50 text-sm px-2 text-center leading-tight select-none drop-shadow-md">
              {emotion.mood}
            </span>
          </div>
        );
      
      case 'diamond':
        return (
          <div className={`${baseClass} rotate-45 rounded-2xl`} style={{ backgroundColor: emotion.color }}>
            <span className="font-medium text-base-50 text-sm px-2 text-center leading-tight -rotate-45 select-none drop-shadow-md">
              {emotion.mood}
            </span>
          </div>
        );
      
      case 'oval':
        return (
          <div className={`${baseClass} rounded-full scale-y-75`} style={{ backgroundColor: emotion.color }}>
            <span className="font-medium text-base-50 text-sm px-2 text-center leading-tight select-none drop-shadow-md">
              {emotion.mood}
            </span>
          </div>
        );
      
      case 'heart':
        return (
          <div className={baseClass}>
            <svg viewBox="0 0 100 100" className="w-full h-full" style={{ fill: emotion.color }}>
              <path d="M50,25 C30,0 0,10 0,35 C0,60 50,90 50,90 C50,90 100,60 100,35 C100,10 70,0 50,25 Z" />
            </svg>
            <span className="absolute inset-0 flex items-center justify-center font-medium text-base-50 text-sm px-2 text-center leading-tight select-none drop-shadow-md">
              {emotion.mood}
            </span>
          </div>
        );
      
      case 'hexagon':
        return (
          <div className={baseClass}>
            <svg viewBox="0 0 100 100" className="w-full h-full" style={{ fill: emotion.color }}>
              <polygon points="50,5 90,25 90,75 50,95 10,75 10,25" />
            </svg>
            <span className="absolute inset-0 flex items-center justify-center font-medium text-base-50 text-sm px-2 text-center leading-tight select-none drop-shadow-md">
              {emotion.mood}
            </span>
          </div>
        );
      
      default: // circle
        return (
          <div className={`${baseClass} rounded-full`} style={{ backgroundColor: emotion.color }}>
            <span className="font-medium text-base-50 text-sm px-2 text-center leading-tight select-none drop-shadow-md">
              {emotion.mood}
            </span>
          </div>
        );
    }
  };
  
  return (
    <div 
      ref={containerRef} 
      className="relative w-full h-[600px] overflow-hidden bg-black/50 rounded-lg"
      {...bindPan()}
      style={{ touchAction: 'none' }}
    >
      {/* Grid background pattern */}
      <div 
        className="absolute inset-0 opacity-10"
        style={{
          backgroundImage: `
            radial-gradient(circle at 1px 1px, white 1px, transparent 1px)
          `,
          backgroundSize: `${GRID_SIZE}px ${GRID_SIZE * HEXAGON_VERTICAL_SPACING}px`,
          backgroundPosition: `${pan.x % GRID_SIZE}px ${pan.y % (GRID_SIZE * HEXAGON_VERTICAL_SPACING)}px`,
          transform: `scale(${zoom})`
        }}
      />
      
      {/* Emotion bubbles */}
      <div 
        className="absolute inset-0 flex items-center justify-center"
        style={{
          transform: `scale(${zoom})`
        }}
      >
        {springs.map((style, i) => {
          const [key, pos] = Array.from(generateBubblePositions.entries())[i];
          if (!key || !pos) return null;
          
          const isActive = focusedBubble === key;
          const emotion = pos.emotion;
          
          return (
            <animated.button
              key={key}
              onClick={(e) => {
                e.stopPropagation();
                if (!isDraggingRef.current && Math.abs(style.scale.get() - 1) < 0.2) {
                  onSelect(emotion.mood);
                }
                isDraggingRef.current = false;
              }}
              style={{
                position: 'absolute',
                width: BUBBLE_SIZE,
                height: BUBBLE_SIZE,
                left: 0,
                top: 0,
                x: style.x.to(x => x + pan.x),
                y: style.y.to(y => y + pan.y),
                scale: style.scale,
                rotate: style.rotation,
                touchAction: 'none',
              }}
              className={`
                cursor-pointer transition-shadow duration-200
                ${isActive ? 'shadow-2xl z-10' : 'shadow-lg'}
              `}
            >
              {renderBubbleShape(emotion, isActive)}
            </animated.button>
          );
        })}
      </div>
      
      {/* Instructions overlay */}
      <div className="absolute bottom-4 left-4 text-base-100/60 text-sm pointer-events-none bg-base-900/80 px-3 py-2 rounded-lg backdrop-blur-sm">
        <p>Drag to pan • Scroll to zoom • Click to select</p>
      </div>
      
      {/* Selected emotion indicator */}
      {focusedBubble && (
        <div className="absolute top-4 left-1/2 -translate-x-1/2 bg-base-900/90 text-base-100 px-4 py-2 rounded-full pointer-events-none backdrop-blur-sm border border-base-800/50">
          {generateBubblePositions.get(focusedBubble)?.emotion.mood}
        </div>
      )}
    </div>
  );
};

// Context Form Component
const ContextForm: React.FC<{
  activities: string[];
  companions: string[];
  selectedActivities: string[];
  selectedCompanions: string[];
  textNote: string;
  onActivitiesChange: (activities: string[]) => void;
  onCompanionsChange: (companions: string[]) => void;
  onTextNoteChange: (note: string) => void;
  onComplete: () => void;
}> = ({
  activities,
  companions,
  selectedActivities,
  selectedCompanions,
  textNote,
  onActivitiesChange,
  onCompanionsChange,
  onTextNoteChange,
  onComplete,
}) => {
  const toggleActivity = (activity: string) => {
    if (selectedActivities.includes(activity)) {
      onActivitiesChange(selectedActivities.filter(a => a !== activity));
    } else {
      onActivitiesChange([...selectedActivities, activity]);
    }
  };

  const toggleCompanion = (companion: string) => {
    if (selectedCompanions.includes(companion)) {
      onCompanionsChange(selectedCompanions.filter(c => c !== companion));
    } else {
      onCompanionsChange([...selectedCompanions, companion]);
    }
  };

  return (
    <div className="space-y-8 max-w-3xl mx-auto">
      {/* Activities */}
      <div>
        <h3 className="text-xl font-semibold text-base-100 mb-4">What are you doing?</h3>
        <div className="flex flex-wrap gap-3">
          {activities.map((activity) => (
            <button
              key={activity}
              onClick={() => toggleActivity(activity)}
              className={`px-6 py-3 rounded-full transition-all duration-200 font-medium
                transform hover:scale-105 active:scale-95 ${
                selectedActivities.includes(activity)
                  ? 'bg-gradient-to-r from-accent-500 to-accent-600 text-base-50 shadow-lg'
                  : 'bg-base-700/50 text-base-300 hover:bg-base-600/50 backdrop-blur-sm'
              }`}
            >
              {activity}
            </button>
          ))}
        </div>
      </div>

      {/* Companions */}
      <div>
        <h3 className="text-xl font-semibold text-base-100 mb-4">Who are you with?</h3>
        <div className="flex flex-wrap gap-3">
          {companions.map((companion) => (
            <button
              key={companion}
              onClick={() => toggleCompanion(companion)}
              className={`px-6 py-3 rounded-full transition-all duration-200 font-medium
                transform hover:scale-105 active:scale-95 ${
                selectedCompanions.includes(companion)
                  ? 'bg-gradient-to-r from-secondary-500 to-secondary-600 text-base-50 shadow-lg'
                  : 'bg-base-700/50 text-base-300 hover:bg-base-600/50 backdrop-blur-sm'
              }`}
            >
              {companion}
            </button>
          ))}
        </div>
      </div>

      {/* Text Note */}
      <div>
        <h3 className="text-xl font-semibold text-base-100 mb-4">Anything else? (optional)</h3>
        <textarea
          value={textNote}
          onChange={(e) => onTextNoteChange(e.target.value)}
          placeholder="Add a note about how you're feeling..."
          className="w-full p-4 bg-base-800/50 backdrop-blur-sm text-base-100 rounded-xl resize-none h-32
            focus:outline-none focus:ring-2 focus:ring-accent-500/50 focus:bg-base-800/70
            placeholder-base-400 transition-all duration-200"
        />
      </div>

      {/* Complete Button */}
      <button
        onClick={onComplete}
        className="w-full py-4 bg-gradient-to-r from-success-500 to-success-600 text-base-50 rounded-xl font-semibold text-lg
          hover:from-success-600 hover:to-success-700 transition-all duration-200 transform hover:scale-[1.02] active:scale-[0.98]
          shadow-lg hover:shadow-xl"
      >
        Complete Check-in
      </button>
    </div>
  );
};

export default EmotionCheckIn;